import { Injectable, signal } from '@angular/core';
import { ALL_CONFIG_ENTRIES, GROUP_DESCRIPTIONS, GROUP_NAMES } from '../config-groups';

/**
 * Generic configuration entry
 */
export interface ConfigEntry<T = any> {
  /** Unique key for the configuration */
  key: string;
  /** Current value */
  value: T;
  /** Human-readable description */
  description: string;
  /** Search tags for filtering in UI */
  searchTags: string[];
  /** Group this config belongs to */
  group: string;
  /** Value type for validation/UI rendering */
  type: 'string' | 'number' | 'boolean' | 'array' | 'object' | 'date';
  /** Default value */
  defaultValue: T;
  /** Last updated timestamp */
  lastUpdated?: Date;
  /** Who last updated it */
  updatedBy?: {
    uid: string;
    email: string;
  };
}

/**
 * Configuration by group
 */
export interface ConfigGroup {
  /** Technical group key */
  key: string;
  /** Display name for UI */
  name: string;
  /** Description of the group */
  description: string;
  /** Config entries in this group */
  entries: ConfigEntry[];
}


@Injectable({
  providedIn: 'root',
})
export class ConfigService {
  private configSignal = signal<ConfigEntry[]>(ALL_CONFIG_ENTRIES);

  /** Read-only access to all configuration */
  public readonly config = this.configSignal.asReadonly();

  /**
   * Get configuration value by key
   */
  getValue<T>(key: string): T | undefined {
    const entry = this.configSignal().find(entry => entry.key === key);
    return entry?.value as T;
  }

  /**
   * Get configuration entry by key
   */
  getEntry(key: string): ConfigEntry | undefined {
    return this.configSignal().find(entry => entry.key === key);
  }

  /**
   * Update configuration value
   */
  updateValue<T>(key: string, value: T, updatedBy?: { uid: string; email: string }): boolean {
    const entries = this.configSignal();
    const index = entries.findIndex(entry => entry.key === key);
    
    if (index === -1) return false;

    const updatedEntries = [...entries];
    updatedEntries[index] = {
      ...updatedEntries[index],
      value,
      lastUpdated: new Date(),
      updatedBy,
    };

    this.configSignal.set(updatedEntries);
    return true;
  }

  /**
   * Get all configuration entries by group
   */
  getByGroup(groupName: string): ConfigEntry[] {
    return this.configSignal().filter(entry => entry.group === groupName);
  }

  /**
   * Get all available groups
   */
  getGroups(): ConfigGroup[] {
    const entries = this.configSignal();
    const groupMap = new Map<string, ConfigEntry[]>();

    // Group entries
    entries.forEach(entry => {
      if (!groupMap.has(entry.group)) {
        groupMap.set(entry.group, []);
      }
      groupMap.get(entry.group)!.push(entry);
    });

    // Convert to ConfigGroup array
    return Array.from(groupMap.entries()).map(([key, entries]) => ({
      key,
      name: this.getGroupName(key),
      description: this.getGroupDescription(key),
      entries: entries.sort((a, b) => a.key.localeCompare(b.key)),
    }));
  }

  /**
   * Search configuration entries
   */
  search(searchTerm: string): ConfigEntry[] {
    const term = searchTerm.toLowerCase();
    return this.configSignal().filter(entry =>
      entry.key.toLowerCase().includes(term) ||
      entry.description.toLowerCase().includes(term) ||
      entry.searchTags.some(tag => tag.toLowerCase().includes(term)) ||
      entry.group.toLowerCase().includes(term)
    );
  }

  /**
   * Reset configuration to defaults
   */
  resetToDefaults(group?: string): void {
    const entries = this.configSignal().map(entry => {
      if (!group || entry.group === group) {
        return {
          ...entry,
          value: entry.defaultValue,
          lastUpdated: new Date(),
        };
      }
      return entry;
    });

    this.configSignal.set(entries);
  }

  /**
   * Add or update configuration entry
   */
  upsertEntry(entry: Omit<ConfigEntry, 'lastUpdated'>): void {
    const entries = this.configSignal();
    const index = entries.findIndex(e => e.key === entry.key);

    if (index === -1) {
      // Add new entry
      this.configSignal.set([...entries, { ...entry, lastUpdated: new Date() }]);
    } else {
      // Update existing entry
      const updatedEntries = [...entries];
      updatedEntries[index] = { ...entry, lastUpdated: new Date() };
      this.configSignal.set(updatedEntries);
    }
  }

  /**
   * Remove configuration entry
   */
  removeEntry(key: string): boolean {
    const entries = this.configSignal();
    const filteredEntries = entries.filter(entry => entry.key !== key);
    
    if (filteredEntries.length === entries.length) {
      return false; // Entry not found
    }

    this.configSignal.set(filteredEntries);
    return true;
  }

  /**
   * Get group display name
   */
  private getGroupName(groupKey: string): string {
    return GROUP_NAMES[groupKey] || groupKey;
  }

  /**
   * Get group description
   */
  private getGroupDescription(groupKey: string): string {
    return GROUP_DESCRIPTIONS[groupKey] || `${groupKey} configuration`;
  }

}